/**
 * Handle OIDC callback - exchange authorization code for tokens
 *
 * This is the host app's responsibility - the app bar only reads the stored token.
 */

import { setSharedAuthToken } from './shared-auth';
import { orgTypeFor } from '@/stores/auth.store';

// Storage key for auth tokens
const AUTH_STORAGE_KEY = 'solid-hub-auth';

/**
 * Get the auth service URL.
 * Uses VITE_AUTH_URL env var, with smart fallback for production.
 */
function getAuthAppUrl(): string {
  // Use env var if set
  const envAuthUrl = import.meta.env.VITE_AUTH_URL;
  if (envAuthUrl) {
    return envAuthUrl;
  }

  // Production fallback: derive auth subdomain from current hostname
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    parts[0] = 'auth';
    return `https://${parts.join('.')}`;
  }

  // Local fallback
  return 'http://localhost:3001';
}

// PKCE utilities
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const hash = await sha256(codeVerifier);
  return base64UrlEncode(hash);
}

export interface AuthCallbackResult {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  error?: string;
}

/**
 * Handle OIDC callback - exchange authorization code for tokens
 */
export async function handleAuthCallback(): Promise<AuthCallbackResult> {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const error = urlParams.get('error');
  const errorDesc = urlParams.get('error_description');

  if (error) {
    return { success: false, error: errorDesc || error };
  }

  if (code) {
    // Get code_verifier from state parameter or sessionStorage
    const stateParam = urlParams.get('state');
    let codeVerifier: string | null = null;
    let redirectUrl = '/';

    if (stateParam) {
      try {
        const decoded = atob(stateParam.replace(/-/g, '+').replace(/_/g, '/'));
        const stateData = JSON.parse(decoded);
        codeVerifier = stateData.cv || null;
        redirectUrl = stateData.redirect || '/';
      } catch {
        redirectUrl = stateParam;
        codeVerifier = sessionStorage.getItem('oidc_code_verifier');
      }
    } else {
      codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    }

    if (!codeVerifier) {
      return { success: false, error: 'Missing PKCE code verifier' };
    }

    try {
      const tokenResponse = await fetch('/oauth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: 'solid-hub',
          code,
          code_verifier: codeVerifier,
          redirect_uri: `${window.location.origin}/auth/callback`,
        }).toString(),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        return { success: false, error: errorData.error_description || errorData.error || 'Token exchange failed' };
      }

      const tokenData = await tokenResponse.json();

      if (tokenData.error) {
        return { success: false, error: tokenData.error_description || tokenData.error };
      }

      const { access_token: accessToken, refresh_token: refreshToken, id_token: idToken } = tokenData;

      if (!accessToken) {
        return { success: false, error: 'No access token received from auth server' };
      }

      // Decode ID token for user info
      let user = null;
      if (idToken) {
        try {
          const parts = idToken.split('.');
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

          // Transform JWT orgs into the frontend User.organizations shape
          const agencyOrgId = import.meta.env.VITE_PRIMARY_AGENCY_ORG_ID;
          const hubAppClaim = (payload.apps || []).find((a: any) => a.client_id === 'solid-hub');
          const hubAppRole = hubAppClaim?.role?.toLowerCase();
          const appRoleMap: Record<string, string> = { admin: 'org_admin', member: 'org_member', owner: 'org_owner' };

          // Role hierarchy for comparing org vs app roles (higher = more privileged)
          const roleLevel: Record<string, number> = {
            'org_owner': 3, 'owner': 3,
            'org_admin': 2, 'admin': 2,
            'org_member': 1, 'member': 1,
          };

          const organizations = (payload.orgs || []).map((org: any) => {
            const isAgencyOrg = org.org_id === agencyOrgId;
            // Only use app role if it elevates the user (never downgrade org_owner to admin)
            const orgLevel = roleLevel[(org.role || '').toLowerCase()] || 0;
            const appLevel = hubAppRole ? (roleLevel[hubAppRole] || 0) : 0;
            // `role` is a nullable column on the membership, so the orgs claim
            // can carry role: null. Calling .toLowerCase() on that threw, the
            // catch below swallowed it, and the user object silently became
            // null — which reads as "not signed in" and bounces the browser
            // back to login forever, with a 200 at every server layer
            // (Angela Accurso, 2026-09-10). One unroled membership must never
            // cost the whole identity: fall back to the least privilege.
            const effectiveRole = (isAgencyOrg && appLevel > orgLevel ? hubAppRole : org.role) || 'member';
            if (!org.role) {
              console.warn(`[AuthCallback] Membership has no role, defaulting to member: org=${org.org_id}`);
            }
            const normalized = String(effectiveRole).toLowerCase();
            return {
              id: org.org_id,
              name: org.name,
              slug: org.slug,
              role: appRoleMap[normalized] || normalized,
              type: orgTypeFor(org.org_id, org.kind),
              kind: org.kind,
            };
          });

          user = {
            id: payload.sub,
            email: payload.email,
            name: payload.name,
            avatarUrl: payload.picture,
            hubRole: hubAppRole ? (appRoleMap[hubAppRole] || hubAppRole) : undefined,
            organizations,
            apps: payload.apps || [],
          };
        } catch (e) {
          // Deliberately not swallowed into `user = null`: the payload below
          // would then be written with isAuthenticated: true and no user, and
          // the app treats that as signed out — an invisible loop. Fail here
          // with something support can act on instead.
          console.error('[AuthCallback] Failed to decode ID token:', e);
          return {
            success: false,
            error: 'We could not read your account details from the sign-in response. Please contact support so we can check your account.',
          };
        }
      }

      // Identity-consistency guard: if this login is a DIFFERENT user than
      // the previous session, remove the legacy 'solid-auth' key too — the
      // app rail reads it before 'solid-hub-auth', so a stale entry would
      // shadow the fresh identity we're about to write.
      try {
        const prev = localStorage.getItem(AUTH_STORAGE_KEY);
        const prevSub = prev ? JSON.parse(prev)?.state?.user?.id : null;
        if (prevSub && user?.id && prevSub !== user.id) {
          console.warn('[AuthCallback] Signed-in user changed; clearing previous session state');
          localStorage.removeItem('solid-auth');
        }
      } catch {
        // Unparseable previous state — it gets overwritten below anyway
      }

      // Store auth data (include idToken so rehydration can decode rich claims)
      const authPayload = JSON.stringify({
        state: { accessToken, idToken: idToken || null, refreshToken: refreshToken || null, user, isAuthenticated: true },
        version: 0,
      });
      try {
        localStorage.setItem(AUTH_STORAGE_KEY, authPayload);
      } catch {
        return {
          success: false,
          error: 'Your browser blocked this site from saving your sign-in. Allow site data for this site, or try a normal (non-private) window.',
        };
      }

      // Read back before reloading. If the token we just wrote is not there,
      // the reload lands unauthenticated and redirects straight back to login —
      // an invisible loop in which every server response is a success. Failing
      // here instead turns that into one actionable message.
      if (localStorage.getItem(AUTH_STORAGE_KEY) !== authPayload) {
        return {
          success: false,
          error: 'Your sign-in could not be saved — your browser is discarding this site\'s data. Allow site data for this site, or try a normal (non-private) window.',
        };
      }

      // Also set shared cookie for cross-subdomain SSO
      setSharedAuthToken(accessToken, refreshToken);

      // Clean up
      sessionStorage.removeItem('oidc_code_verifier');
      sessionStorage.removeItem('oidc_state');

      // Redirect to original destination
      window.location.href = redirectUrl;

      return { success: true, accessToken, refreshToken };
    } catch (err) {
      console.error('[AuthCallback] Token exchange error:', err);
      return { success: false, error: 'Token exchange failed' };
    }
  }

  return { success: false };
}

/**
 * Get the OIDC authorization URL for login with PKCE
 */
export async function getAuthLoginUrl(redirectUrl?: string): Promise<string> {
  const authBaseUrl = getAuthAppUrl();
  const redirect = redirectUrl || window.location.origin;

  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Encode code_verifier in state parameter
  const stateData = JSON.stringify({ redirect, cv: codeVerifier });
  const stateEncoded = btoa(stateData).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  // Also store in sessionStorage as fallback
  sessionStorage.setItem('oidc_code_verifier', codeVerifier);
  sessionStorage.setItem('oidc_state', redirect);

  const url = new URL(`${authBaseUrl}/oauth/authorize`);
  url.searchParams.set('client_id', 'solid-hub');
  url.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email orgs apps offline_access');
  // Auth server issues refresh tokens only when offline_access arrives as an
  // extra param — oidc-provider strips it from the scope string.
  url.searchParams.set('offline_access', 'true');
  url.searchParams.set('state', stateEncoded);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');

  return url.toString();
}
